import { create } from 'zustand';
import * as api from '../lib/api';
import type { Agent } from '../lib/api';

interface AgentState {
  agents: Agent[];
  /** True once a `loadAgents` has failed and no later one has succeeded. */
  loadFailed: boolean;

  setAgents: (agents: Agent[]) => void;
  addAgent: (agent: Agent) => void;
  removeAgent: (id: string) => void;
  reset: () => void;

  /**
   * Refreshes the owner's agent list. It never rejects; a failure leaves the
   * previous list in place and raises `loadFailed`, which is what tells a list
   * consumer that an empty list means "we do not know" rather than "none".
   */
  loadAgents: () => Promise<void>;
}

export const useAgentStore = create<AgentState>()((set) => ({
  agents: [],
  loadFailed: false,

  setAgents: (agents) => set({ agents }),

  addAgent: (agent) => set((s) => {
    if (s.agents.some(a => a.id === agent.id)) return s;
    return { agents: [...s.agents, agent] };
  }),

  removeAgent: (id) => set((s) => ({
    agents: s.agents.filter(a => a.id !== id),
  })),

  loadAgents: async () => {
    try {
      const agents = await api.fetchAgents();
      set({ agents, loadFailed: false });
    } catch {
      set({ loadFailed: true });
    }
  },

  reset: () => set({ agents: [], loadFailed: false }),
}));
